import {CommonModule} from '@angular/common';
import {HTTP_INTERCEPTORS, HttpBackend, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {ErrorHandler, inject, NgModule, Optional, provideAppInitializer, SkipSelf} from '@angular/core';
import {Router} from '@angular/router';
import {provideTranslateService, TranslateLoader, TranslateModule, TranslateService} from '@ngx-translate/core';
import * as Sentry from "@sentry/angular";
import {environment} from 'src/environments/environment';
import {ConfigService} from './config';
import {LoaderModule} from './loader/loader.module';
import {HttpErrorInterceptor} from "./interceptors/http-error-interceptor.service";
import {GlobalErrorHandler} from "./handlers/globar-error.handler";
import {IntegrationService} from "./integration/integration.service";
import {StoreService} from "../shared/services/store.service";
import {MatomoInitializerService, provideMatomo, withRouteData, withRouter} from "ngx-matomo-client";
import packageJson from 'package.json'
import {catchError, filter, forkJoin, from, map, of, switchMap, tap, throwError} from "rxjs";
import {ReferenceApiService} from "./api/reference-api/reference-api.service";
import {Utils} from "../shared/utils/generics.utils";
import {getSupportedLanguage, UiUtils} from "../shared/utils/ui.utils";
import {Company} from "./models/company";
import {ApiService} from "./api/api.service";
import {ExternalApiService} from "./api/external-api/external-api.service";
import {CountrySettingsDto} from "./dto/country-settings-dto";
import {CustomTranslateHttpLoader, TranslationResource} from "../shared/utils/custom-translate-http-loader";
import {ServiceInterceptor} from "./interceptors/service.interceptor";
import {Context} from "./models/context";
import {Vehicle} from "./models/vehicle";
import {isEmpty} from "lodash";
import moment from "moment";
import 'moment/min/locales';
import {LanguageService} from "../shared/services/language.service";

const replacePrivateDataFromPath = (path: string) => {
  if (path?.includes('license=')) {
    return path.replace(/license=([^&]*)/, 'license=<LICENSE>');
  }

  if (path?.includes('postcode=')) {
    return path.replace(/postcode=([^&]*)/, 'postcode=<POSTCODE>');
  }

  if (path?.includes('email=')) {
    return path.replace(/email=([^&]*)/, 'email=<EMAIL>');
  }

  if (path?.includes('phone=')) {
    return path.replace(/phone=([^&]*)/, 'phone=<PHONE>');
  }
  return path;
}

const initializeApp = (
  config: ConfigService,
  referenceApi: ReferenceApiService,
  apiService: ApiService,
  externalApiService: ExternalApiService,
  matomoInitializer: MatomoInitializerService,
  store: StoreService
) =>
  from(config.loadConfig()).pipe(
    map(() => {
      const _config = config.getConfig();

      Sentry.init({
        enabled: _config.app.environment === 'prod' || _config.webapp.enableSentry,
        environment: _config.app.environment,
        dsn: 'https://00011b0ec7424dee86ef34ccedab4ec7@o1145449.ingest.sentry.io/6238717',
        integrations: (integrations) => {
          integrations.push(Sentry.browserTracingIntegration())
          return integrations;
        },
        release: packageJson.version,
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        normalizeDepth: 5,
        ignoreErrors: [
          '0 Unknown Error'
        ],
        // Important - GDPR - License is a personal data we don't want to pass to Sentry.
        // We exclude it from navigation path & http request.
        beforeBreadcrumb(breadcrumb) {
          // HTTP Request removal
          if (breadcrumb.category === 'xhr') {
            const url: string = breadcrumb?.data?.['url'];
            if (url.includes('licenses')) {
              if (breadcrumb && breadcrumb.data && breadcrumb.data['url']) {
                breadcrumb.data['url'] = url.replace(/licenses\/.*/, 'licenses/<LICENSE>');
              }

            }
          }

          // Navigation removal
          if (breadcrumb.category === 'navigation') {
            if (breadcrumb && breadcrumb.data && breadcrumb.data['from']) {
              breadcrumb.data['from'] = replacePrivateDataFromPath(breadcrumb.data['from']);
            }

            if (breadcrumb && breadcrumb.data && breadcrumb.data['to']) {
              breadcrumb.data['to'] = replacePrivateDataFromPath(breadcrumb.data['to']);
            }
          }
          return breadcrumb;
        },
      })
      referenceApi.setConfig(_config);
      externalApiService.setConfig(_config);
      apiService.setConfig(_config);

      preconnectConfigurationS3();

      return _config;
    }),
    switchMap(() => initMatomo(externalApiService, referenceApi, matomoInitializer, store)),
    switchMap(() => initCountrySettings(externalApiService, store))
  );

const initMatomo = (externalApiService: ExternalApiService, referenceApi: ReferenceApiService, matomoInitializer: MatomoInitializerService, store: StoreService) => {
  return externalApiService.getCompany(Utils.getCompanyFromDomain(), getSupportedLanguage()).pipe(
    map(dto => {
      const company = new Company(dto);
      store.setCompany(company);
      return company;
    }),
    switchMap(company => referenceApi.getMatomoId(company.id)),
    catchError((error) => {
      if (error.status === 404) {
        matomoInitializer.initializeTracker({
          siteId: '1',
          trackerUrl: getMatomoTrackerUrl()
        })
        store.setMatomoSiteId(1);
        return of(undefined);
      }
      return throwError(() => error);
    }),
    tap((response) => {
      if (response?.body) {
        matomoInitializer.initializeTracker({
          siteId: response.body.matomoId,
          trackerUrl: getMatomoTrackerUrl()
        })
        store.setMatomoSiteId(response.body.matomoId);
      }
    }))
};

const initCountrySettings = (
  externalApiService: ExternalApiService,
  store: StoreService
) => {
  const countrySettings$ = Object.values(Context)
    .map((context) => window.localStorage.getItem(`${context}.vehicle`))
    .filter(Boolean)
    .map((local) => {
      try {
        return JSON.parse(local!) as Vehicle;
      } catch (e) {
        return undefined;
      }
    })
    .filter((vehicle): vehicle is Vehicle => !!vehicle)
    .map((vehicle) => vehicle?.license?.countryISO)
    .filter((iso): iso is string => !!iso)
    .filter((value, index, self) => self.indexOf(value) === index)
    .map((country) => externalApiService.getCountrySettings(country).pipe(
        map((countrySettings): [string, CountrySettingsDto] => [country!, countrySettings]),
        catchError((err) => {
          if (err.status !== 0) {
            Sentry.captureException(new Error('Unable to get country settings'), {extra: {country}})
          }
          return of(undefined);
        }),
        filter(countrySetting => !!countrySetting),
      )
    );

  return isEmpty(countrySettings$) ? of(undefined) : forkJoin(countrySettings$).pipe(
    tap((validSettings) => store.setCountrySettings(new Map(validSettings))),
    catchError((err) => {
      Sentry.captureException(new Error('Unable to init country settings'), {
        extra: {error: err},
      });
      return of([]);
    })
  );
};

const initLanguage = async () => {
  const languageService = inject(LanguageService);
  const translate = inject(TranslateService);

  const langParam = getLangFromUrl();
  const selectedLang = langParam ?? languageService.getLanguage();
  const supportedLang = getSupportedLanguage(selectedLang);

  await UiUtils.registerLocale(supportedLang);

  translate.setDefaultLang(UiUtils.FALLBACK_LANG);
  moment.locale(supportedLang);

  languageService.setLanguage(supportedLang);
};

const getLangFromUrl = (): string | null => {
  // We cannot use URL.search params because we're in 'useHash: true' mode.
  const hash = window.location.hash;
  const match = hash.match(/[?&]lang=([^&]+)/);
  return match ? UiUtils.formatLocale(decodeURIComponent(match[1])) : null;
}

const getMatomoTrackerUrl = () => {
  return 'https://carool.matomo.cloud/';
}

const isDevelopment = () => {
  return ['localhost', '.dev.', '.val.'].some(t => window.location.host.includes(t));
}

const preconnectConfigurationS3 = () => {
  const host = window.location.host;
  let env = host.includes('localhost') ? 'dev' : host.split('.')[1];

  if (!['dev', 'val'].some(e => env === e)) {
    env = 'prod';
  }

  const link = document.createElement('link');
  link.href = `https://carool-configuration-${env}.s3.eu-west-1.amazonaws.com`;
  link.rel = 'preconnect';
  document.querySelector('head')?.appendChild(link)
}

@NgModule({
  declarations: [],
  exports: [
    TranslateModule,
    LoaderModule
  ], imports: [
    // Angular
    CommonModule,
    // CaRool
    LoaderModule
  ], providers: [
    ConfigService,
    provideAppInitializer(() => initializeApp(inject(ConfigService), inject(ReferenceApiService), inject(ApiService), inject(ExternalApiService), inject(MatomoInitializerService), inject(StoreService))),
    provideAppInitializer(() => initLanguage()),
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService)
    }),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServiceInterceptor,
      multi: true
    },
    provideAppInitializer(() => {
      inject(IntegrationService)
    }),
    provideMatomo(
      {
        mode: "deferred",
        requireConsent: "cookie",
        disabled: isDevelopment()
      },
      withRouter(),
      withRouteData()
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideTranslateService({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateHttpLoader,
        deps: [HttpBackend, ConfigService]
      }
    })
  ]
})
export class CoreModule {
  constructor(
    @Optional()
    @SkipSelf()
    parentModule: CoreModule
  ) {
    if (parentModule) {
      throw new Error('CoreModule is already loaded. Import only in AppModule.');
    }
  }
}

